import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import clsx from "clsx";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import BarChartIcon from "@mui/icons-material/BarChart";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import HeadsetIcon from "@mui/icons-material/Headset";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import MicIcon from "@mui/icons-material/Mic";
import MoodIcon from "@mui/icons-material/Mood";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import PersonIcon from "@mui/icons-material/Person";
import SendIcon from "@mui/icons-material/Send";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import {
  Dialog,
  FormControlLabel,
  Menu,
  MenuItem,
  Switch,
} from "@mui/material";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { green } from "@mui/material/colors";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { makeStyles } from "tss-react/mui";

import { AuthContext } from "../../context/Auth/AuthContext";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { SearchMessageContext } from "../../context/SearchMessage/SearchMessageContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import RecordingTimer from "./RecordingTimer";

let Mp3Recorder = null;

const initRecorder = async () => {
  if (!Mp3Recorder) {
    try {
      const MicRecorder = (await import("mic-recorder-to-mp3")).default;
      Mp3Recorder = new MicRecorder({ bitRate: 128 });
    } catch (error) {
      console.error("Failed to initialize recorder:", error);
      return null;
    }
  }
  return Mp3Recorder;
};

const useStyles = makeStyles()((theme) => ({
  mainWrapper: {
    background: theme.palette.background.default,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      bottom: 0,
      width: "100%",
    },
  },

  newMessageBox: {
    background: theme.palette.background.default,
    width: "100%",
    display: "flex",
    padding: "7px",
    alignItems: "center",
  },

  messageInputWrapper: {
    padding: 6,
    marginRight: 7,
    background: theme.palette.background.paper,
    display: "flex",
    borderRadius: 20,
    flex: 1,
    position: "relative",
  },

  messageInput: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
  },

  sendMessageIcons: {
    color: "grey",
  },

  uploadInput: {
    display: "none",
  },

  viewMediaInputWrapper: {
    display: "flex",
    padding: "10px 13px",
    position: "relative",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.background.default,
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
  },

  attachmentPreviewBar: {
    display: "flex",
    flexDirection: "column",
    padding: "10px 13px",
    backgroundColor: theme.palette.background.default,
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
  },

  attachmentPreviewList: {
    display: "flex",
    overflowX: "auto",
    gap: "8px",
    paddingBottom: "8px",
    "&::-webkit-scrollbar": { height: 4 },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: 2,
      backgroundColor: "rgba(0,0,0,0.2)",
    },
  },

  attachmentPreviewTile: {
    position: "relative",
    width: 80,
    height: 80,
    borderRadius: 8,
    overflow: "hidden",
    flexShrink: 0,
    backgroundColor: "rgba(0,0,0,0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid rgba(0,0,0,0.1)",
  },

  attachmentPreviewActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  emojiBox: {
    position: "absolute",
    bottom: "100%",
    left: 0,
    zIndex: 1300,
    borderTop: "1px solid #e8e8e8",
    [theme.breakpoints.down("sm")]: {
      left: 0,
      right: 0,
      "& em-emoji-picker": {
        width: "100% !important",
        maxWidth: "100vw",
      },
    },
  },

  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },

  audioLoading: {
    color: green[500],
    opacity: "70%",
  },

  recorderWrapper: {
    display: "flex",
    alignItems: "center",
    alignContent: "middle",
  },

  cancelAudioIcon: {
    color: "red",
  },

  sendAudioIcon: {
    color: "green",
  },

  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 8,
    paddingLeft: 73,
    paddingRight: 7,
  },

  replyginMsgContainer: {
    flex: 1,
    marginRight: 5,
    overflowY: "hidden",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  replyginMsgBody: {
    padding: 10,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  replyginContactMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.secondary.main,
  },

  replyginSelfMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.primary.main,
  },

  messageContactName: {
    display: "flex",
    color: theme.palette.secondary.light,
    fontWeight: 500,
  },
  messageQuickAnswersWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: theme.palette.background.paper,
    padding: "2px",
    border: `1px solid ${theme.palette.divider}`,
    left: 0,
    width: "100%",
    "& li": {
      listStyle: "none",
      "& a": {
        display: "block",
        padding: "8px",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxHeight: "32px",
        color: theme.palette.text.primary,
        textDecoration: "none",
        "&:hover": {
          background: theme.palette.action.hover,
          cursor: "pointer",
        },
      },
    },
  },
}));

const AttachIconBox = ({ bgcolor, icon }) => (
  <Box
    sx={{
      width: 36,
      height: 36,
      borderRadius: "10px",
      bgcolor,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      mr: 1.5,
      flexShrink: 0,
    }}
  >
    {icon}
  </Box>
);

const KeyboardArrowListener = ({ count, selectedIndex, setSelectedIndex }) => {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowLeft") {
        setSelectedIndex((i) => (i - 1 + count) % count);
      } else if (e.key === "ArrowRight") {
        setSelectedIndex((i) => (i + 1) % count);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [count, setSelectedIndex]);
  return null;
};

const MessageInput = ({ ticketStatus }) => {
  const { classes } = useStyles();
  const { ticketId } = useParams();

  const [medias, setMedias] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const [attachMenuAnchorEl, setAttachMenuAnchorEl] = useState(null);

  const inputRef = useRef();
  const documentInputRef = useRef();
  const photoInputRef = useRef();
  const contactInputRef = useRef();
  const emojiPickerRef = useRef();
  const emojiButtonRef = useRef();

  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { inputFocusNonce } = useContext(SearchMessageContext) || {};
  const { user } = useContext(AuthContext);

  useEffect(() => {
    if (!showEmoji) return;
    const handleMouseDown = (e) => {
      const insidePicker = emojiPickerRef.current?.contains(e.target);
      const insideButton = emojiButtonRef.current?.contains(e.target);
      if (!insidePicker && !insideButton) {
        setShowEmoji(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [showEmoji]);

  const [previewUrls, setPreviewUrls] = useState([]);
  const captionRef = useRef(null);
  const captionsRef = useRef([]);   // per-file captions, no re-renders on type
  const prevIndexRef = useRef(0);
  const [pdfThumbnail, setPdfThumbnail] = useState(null);
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (medias.length === 0) {
      setPreviewUrls([]);
      setSelectedIndex(0);
      captionsRef.current = [];
      if (captionRef.current) captionRef.current.value = "";
      prevIndexRef.current = 0;
      return;
    }
    setSelectedIndex((prev) => Math.min(prev, medias.length - 1));
    const urls = medias.map((f) => URL.createObjectURL(f));
    setPreviewUrls(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [medias]);

  // Save caption for the file we're leaving; restore caption for the new file
  useEffect(() => {
    const prev = prevIndexRef.current;
    if (prev !== selectedIndex && captionRef.current) {
      captionsRef.current[prev] = captionRef.current.value;
      captionRef.current.value = captionsRef.current[selectedIndex] || "";
    }
    prevIndexRef.current = selectedIndex;
  }, [selectedIndex]);

  useEffect(() => {
    const primaryFile = medias[0];
    if (!primaryFile || primaryFile.type !== "application/pdf") {
      setPdfThumbnail(null);
      setPdfPageCount(0);
      return;
    }
    let cancelled = false;
    const render = async () => {
      try {
        const pdfjsLib = await import("pdfjs-dist");
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
          "pdfjs-dist/build/pdf.worker.min.mjs",
          import.meta.url,
        ).href;
        const arrayBuffer = await primaryFile.arrayBuffer();
        if (cancelled) return;
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        if (cancelled) return;
        setPdfPageCount(pdf.numPages);
        const page = await pdf.getPage(1);
        if (cancelled) return;
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport })
          .promise;
        if (!cancelled) setPdfThumbnail(canvas.toDataURL("image/png"));
      } catch (err) {
        console.error("PDF render error:", err);
      }
    };
    render();
    return () => {
      cancelled = true;
    };
  }, [medias]);

  useEffect(() => {
    inputRef.current.focus();
  }, [replyingMessage]);

  // Return focus to the message input after a search jump lands on a message
  useEffect(() => {
    if (inputFocusNonce) inputRef.current?.focus();
  }, [inputFocusNonce]);

  useEffect(() => {
    inputRef.current.focus();
    return () => {
      setInputMessage("");
      setShowEmoji(false);
      captionsRef.current = [];
      setMedias([]);
      if (captionRef.current) captionRef.current.value = "";
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  const handleChangeInput = (e) => {
    setInputMessage(e.target.value);
    handleLoadQuickAnswer(e.target.value);
  };

  const handleQuickAnswersClick = (value) => {
    setInputMessage(value);
    setTypeBar(false);
  };

  const handleAddEmoji = (e) => {
    let emoji = e.native;
    setInputMessage((prevState) => prevState + emoji);
  };

  const handleChangeMedias = (e) => {
    if (!e.target.files) return;
    const selectedMedias = Array.from(e.target.files);
    setMedias((prev) => [...prev, ...selectedMedias]);
    e.target.value = "";
  };

  const handleInputPaste = (e) => {
    if (e.clipboardData.files[0]) {
      setMedias([e.clipboardData.files[0]]);
    }
  };

  const handleUploadMedia = async () => {
    // Persist the caption currently visible in the input before reading all captions
    if (captionRef.current) {
      captionsRef.current[selectedIndex] = captionRef.current.value;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("fromMe", true);
    medias.forEach((media, i) => {
      formData.append("medias", media);
      const caption = captionsRef.current[i];
      formData.append("body", caption || media.name);
    });
    try {
      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
    captionsRef.current = [];
    setMedias([]);
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "") return;
    setLoading(true);

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: inputMessage.trim(),
      quotedMsg: replyingMessage,
    };
    try {
      await api.post(`/messages/${ticketId}`, message);
    } catch (err) {
      toastError(err);
    }

    setInputMessage("");
    setShowEmoji(false);
    setLoading(false);
    setReplyingMessage(null);
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      const recorder = await initRecorder();
      if (!recorder) throw new Error("Recorder not available");
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await recorder.start();
      setRecording(true);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLoadQuickAnswer = async (value) => {
    if (value && value.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", {
          params: { searchParam: inputMessage.substring(1) },
        });
        setQuickAnswer(data.quickAnswers);
        if (data.quickAnswers.length > 0) {
          setTypeBar(true);
        } else {
          setTypeBar(false);
        }
      } catch (err) {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  };

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      const recorder = await initRecorder();
      if (!recorder) throw new Error("Recorder not available");
      const [, blob] = await recorder.stop().getMp3();
      if (blob.size < 10000) {
        setLoading(false);
        setRecording(false);
        return;
      }

      const formData = new FormData();
      const filename = `${new Date().getTime()}.mp3`;
      formData.append("medias", blob, filename);
      formData.append("body", filename);
      formData.append("fromMe", true);

      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    try {
      const recorder = await initRecorder();
      if (recorder) await recorder.stop().getMp3();
      setRecording(false);
    } catch (err) {
      toastError(err);
    }
  };


  const iconStyle = { color: "#fff", fontSize: 20 };

  const attachmentOptions = [
    {
      key: "document",
      label: "Document",
      icon: <InsertDriveFileIcon sx={iconStyle} />,
      iconBg: "#5157AE",
      enabled: true,
      onClick: () => documentInputRef.current?.click(),
    },
    {
      key: "photos",
      label: "Photos & Videos",
      icon: <PermMediaIcon sx={iconStyle} />,
      iconBg: "#069E5A",
      enabled: true,
      onClick: () => photoInputRef.current?.click(),
    },
    {
      key: "camera",
      label: "Camera",
      icon: <CameraAltIcon sx={iconStyle} />,
      iconBg: "#D3284A",
      enabled: false,
    },
    {
      key: "audio",
      label: "Audio",
      icon: <HeadsetIcon sx={iconStyle} />,
      iconBg: "#E07717",
      enabled: false,
    },
    {
      key: "contact",
      label: "Contact",
      icon: <PersonIcon sx={iconStyle} />,
      iconBg: "#009EE2",
      enabled: true,
      onClick: () => contactInputRef.current?.click(),
    },
    {
      key: "poll",
      label: "Poll",
      icon: <BarChartIcon sx={iconStyle} />,
      iconBg: "#C28700",
      enabled: false,
    },
    {
      key: "event",
      label: "Event",
      icon: <CalendarTodayIcon sx={iconStyle} />,
      iconBg: "#D33B3B",
      enabled: false,
    },
    {
      key: "sticker",
      label: "New sticker",
      icon: <SentimentSatisfiedAltIcon sx={iconStyle} />,
      iconBg: "#00B5A3",
      enabled: false,
    },
  ];

  const renderReplyingMessage = (message) => {
    return (
      <div className={classes.replyginMsgWrapper}>
        <div className={classes.replyginMsgContainer}>
          <span
            className={clsx(classes.replyginContactMsgSideColor, {
              [classes.replyginSelfMsgSideColor]: !message.fromMe,
            })}
          ></span>
          <div className={classes.replyginMsgBody}>
            {!message.fromMe && (
              <span className={classes.messageContactName}>
                {message.contact?.name}
              </span>
            )}
            {message.body}
          </div>
        </div>
        <IconButton
          aria-label="showRecorder"
          component="span"
          disabled={loading || ticketStatus !== "open"}
          onClick={() => setReplyingMessage(null)}
        >
          <ClearIcon className={classes.sendMessageIcons} />
        </IconButton>
      </div>
    );
  };

  const closePreview = () => {
    captionsRef.current = [];
    setMedias([]);
  };

  const handleRemoveMedia = (index) => {
    if (medias.length === 1) {
      closePreview();
      return;
    }

    // Flush the currently visible caption before mutating the array
    if (captionRef.current) {
      captionsRef.current[selectedIndex] = captionRef.current.value;
    }

    // Remove the caption entry for the deleted file
    captionsRef.current.splice(index, 1);

    // Decide which file to show next
    const newSelected =
      index < selectedIndex
        ? selectedIndex - 1          // removed a file before current → shift down
        : Math.min(selectedIndex, medias.length - 2); // removed current or after → clamp

    // Update the input to show the new file's caption without triggering the
    // save/restore effect (set prevIndexRef so the effect sees no change)
    if (captionRef.current) {
      captionRef.current.value = captionsRef.current[newSelected] || "";
    }
    prevIndexRef.current = newSelected;

    setSelectedIndex(newSelected);
    setMedias((prev) => prev.filter((_, i) => i !== index));
  };

  const primaryFile = medias[0];
  const selectedFile = medias[selectedIndex] ?? primaryFile;
  const isPdf = selectedFile?.type === "application/pdf";
  const isImage = selectedFile?.type.startsWith("image/");
  const isVideo = selectedFile?.type.startsWith("video/");

  return (
    <>
      {/* Hidden file inputs — always in DOM so refs work inside Dialog too */}
      <input
        ref={documentInputRef}
        type="file"
        style={{ display: "none" }}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.csv,.rtf"
        onChange={handleChangeMedias}
      />
      <input
        ref={photoInputRef}
        type="file"
        style={{ display: "none" }}
        accept="image/*,video/*"
        multiple
        onChange={handleChangeMedias}
      />
      <input
        ref={contactInputRef}
        type="file"
        style={{ display: "none" }}
        accept=".vcf"
        onChange={handleChangeMedias}
      />

      {/* Arrow-key navigation for attachment preview */}
      {medias.length > 0 && (
        <KeyboardArrowListener
          count={medias.length}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
        />
      )}

      {/* Full-screen attachment preview */}
      <Dialog
        open={medias.length > 0}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: "#111827",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 1.5,
            py: 1,
            bgcolor: "#1f2937",
          }}
        >
          <IconButton onClick={closePreview} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
          <Box sx={{ ml: 1.5, overflow: "hidden" }}>
            <Typography
              sx={{ color: "#fff", fontWeight: 500, fontSize: 15 }}
              noWrap
            >
              {selectedFile?.name}
            </Typography>
            {isPdf && pdfPageCount > 0 && (
              <Typography
                sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12 }}
              >
                {pdfPageCount} {pdfPageCount === 1 ? "page" : "pages"}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Large preview */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            p: 3,
          }}
        >
          {isPdf &&
            (pdfThumbnail ? (
              <Box
                component="img"
                src={pdfThumbnail}
                alt="PDF preview"
                sx={{
                  maxHeight: "100%",
                  maxWidth: "100%",
                  objectFit: "contain",
                  borderRadius: 1,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
                }}
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <CircularProgress sx={{ color: "rgba(255,255,255,0.7)" }} />
                <Typography
                  sx={{ color: "rgba(255,255,255,0.5)", fontSize: 14 }}
                >
                  Loading preview…
                </Typography>
              </Box>
            ))}
          {isImage && previewUrls[selectedIndex] && (
            <Box
              component="img"
              src={previewUrls[selectedIndex]}
              alt={selectedFile?.name}
              sx={{
                maxHeight: "100%",
                maxWidth: "100%",
                objectFit: "contain",
                borderRadius: 1,
              }}
            />
          )}
          {isVideo && previewUrls[selectedIndex] && (
            <video
              key={previewUrls[selectedIndex]}
              src={previewUrls[selectedIndex]}
              controls
              style={{ maxHeight: "100%", maxWidth: "100%", borderRadius: 8 }}
            />
          )}
          {!isPdf && !isImage && !isVideo && selectedFile && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <InsertDriveFileIcon
                sx={{ fontSize: 80, color: "rgba(255,255,255,0.6)" }}
              />
              <Typography
                sx={{ color: "#fff", fontWeight: 500, textAlign: "center" }}
                noWrap
              >
                {selectedFile.name}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Caption input */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            bgcolor: "#1f2937",
          }}
        >
          <InputBase
            fullWidth
            placeholder="Add a caption…"
            inputRef={captionRef}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey) handleUploadMedia();
            }}
            sx={{
              color: "#fff",
              bgcolor: "rgba(255,255,255,0.08)",
              borderRadius: 20,
              px: 2,
              py: 0.75,
              fontSize: 14,
              "& input::placeholder": { color: "rgba(255,255,255,0.4)" },
            }}
          />
        </Box>

        {/* Thumbnail strip + send */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            py: 1.5,
            gap: 1,
            bgcolor: "#111827",
          }}
        >
          <Box sx={{ display: "flex", gap: 1, flex: 1, overflowX: "auto" }}>
            {medias.map((file, i) => {
              const fileIsImg = file.type.startsWith("image/");
              const fileIsVid = file.type.startsWith("video/");
              const fileIsPdf = file.type === "application/pdf";
              const thumbSrc =
                fileIsPdf && i === 0 ? pdfThumbnail : previewUrls[i];
              return (
                <Box key={i} sx={{ position: "relative", flexShrink: 0 }}>
                  {/* Thumbnail */}
                  <Box
                    onClick={() => setSelectedIndex(i)}
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 1.5,
                      overflow: "hidden",
                      bgcolor: "rgba(255,255,255,0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      border: i === selectedIndex ? "2px solid #00a884" : "2px solid transparent",
                      transition: "border-color 0.15s",
                    }}
                  >
                    {(fileIsImg || fileIsPdf) && thumbSrc ? (
                      <img
                        src={thumbSrc}
                        alt={file.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : fileIsVid && previewUrls[i] ? (
                      <video
                        src={previewUrls[i]}
                        muted
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <InsertDriveFileIcon sx={{ color: "rgba(255,255,255,0.6)", fontSize: 24 }} />
                    )}
                  </Box>

                  {/* Remove button */}
                  <Box
                    onClick={(e) => { e.stopPropagation(); handleRemoveMedia(i); }}
                    sx={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      bgcolor: "rgba(0,0,0,0.75)",
                      border: "1.5px solid rgba(255,255,255,0.25)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      zIndex: 1,
                      "&:hover": { bgcolor: "#ef4444" },
                      transition: "background-color 0.15s",
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 11, color: "#fff" }} />
                  </Box>
                </Box>
              );
            })}
            <Box
              onClick={() => {
                const f = medias[0];
                if (
                  f?.type.startsWith("image/") ||
                  f?.type.startsWith("video/")
                ) {
                  photoInputRef.current?.click();
                } else {
                  documentInputRef.current?.click();
                }
              }}
              sx={{
                width: 52,
                height: 52,
                borderRadius: 1.5,
                flexShrink: 0,
                bgcolor: "rgba(255,255,255,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                border: "2px dashed rgba(255,255,255,0.2)",
              }}
            >
              <AddIcon sx={{ color: "rgba(255,255,255,0.5)" }} />
            </Box>
          </Box>

          <IconButton
            onClick={handleUploadMedia}
            disabled={loading}
            sx={{
              bgcolor: "#00a884",
              width: 52,
              height: 52,
              flexShrink: 0,
              "&:hover": { bgcolor: "#00c79a" },
              "&.Mui-disabled": { bgcolor: "rgba(0,168,132,0.4)" },
            }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              <SendIcon sx={{ color: "#fff" }} />
            )}
          </IconButton>
        </Box>
      </Dialog>

      {/* Normal message input */}
      <Paper square elevation={0} className={classes.mainWrapper}>
        {/* Attachment popup menu */}
        <Menu
          anchorEl={attachMenuAnchorEl}
          open={Boolean(attachMenuAnchorEl)}
          onClose={() => setAttachMenuAnchorEl(null)}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 230 } } }}
        >
          {attachmentOptions.map((opt) => (
            <MenuItem
              key={opt.key}
              disabled={!opt.enabled}
              dense
              onClick={() => {
                if (opt.enabled && opt.onClick) opt.onClick();
                setAttachMenuAnchorEl(null);
              }}
            >
              <AttachIconBox
                bgcolor={opt.enabled ? opt.iconBg : "rgba(128,128,128,0.3)"}
                icon={opt.icon}
              />
              <ListItemText
                primary={opt.label}
                slotProps={{ primary: { variant: "body2" } }}
              />
            </MenuItem>
          ))}
        </Menu>

        {replyingMessage && renderReplyingMessage(replyingMessage)}

        {showEmoji && (
          <div ref={emojiPickerRef} className={classes.emojiBox}>
            <Picker
              data={data}
              perLine={9}
              previewPosition="none"
              skinTonePosition="none"
              onEmojiSelect={handleAddEmoji}
            />
          </div>
        )}

        <div className={classes.newMessageBox}>
          {/* Attachment button */}
          <IconButton
            aria-label="attach"
            disabled={loading || recording || ticketStatus !== "open"}
            onClick={(e) => setAttachMenuAnchorEl(e.currentTarget)}
          >
            <AddIcon className={classes.sendMessageIcons} />
          </IconButton>

          <div className={classes.messageInputWrapper}>
            <IconButton
              ref={emojiButtonRef}
              aria-label="emojiPicker"
              size="small"
              disabled={loading || recording || ticketStatus !== "open"}
              onClick={() => setShowEmoji((prev) => !prev)}
              sx={{ color: "text.secondary", mr: 0.5, flexShrink: 0 }}
            >
              <MoodIcon fontSize="small" />
            </IconButton>
            <InputBase
              inputRef={(input) => {
                input && input.focus();
                input && (inputRef.current = input);
              }}
              className={classes.messageInput}
              placeholder={
                ticketStatus === "open"
                  ? i18n.t("messagesInput.placeholderOpen")
                  : i18n.t("messagesInput.placeholderClosed")
              }
              multiline
              maxRows={5}
              value={inputMessage}
              onChange={handleChangeInput}
              disabled={recording || loading || ticketStatus !== "open"}
              onPaste={(e) => {
                ticketStatus === "open" && handleInputPaste(e);
              }}
              onKeyPress={(e) => {
                if (loading || e.shiftKey) return;
                else if (e.key === "Enter") handleSendMessage();
              }}
            />
            {typeBar ? (
              <ul className={classes.messageQuickAnswersWrapper}>
                {quickAnswers.map((value, index) => (
                  <li
                    className={classes.messageQuickAnswersWrapperItem}
                    key={index}
                  >
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a onClick={() => handleQuickAnswersClick(value.message)}>
                      {`${value.shortcut} - ${value.message}`}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <div />
            )}
          </div>

          {inputMessage ? (
            <IconButton
              aria-label="sendMessage"
              component="span"
              onClick={handleSendMessage}
              disabled={loading}
            >
              <SendIcon className={classes.sendMessageIcons} />
            </IconButton>
          ) : recording ? (
            <div className={classes.recorderWrapper}>
              <IconButton
                aria-label="cancelRecording"
                component="span"
                fontSize="large"
                disabled={loading}
                onClick={handleCancelAudio}
              >
                <HighlightOffIcon className={classes.cancelAudioIcon} />
              </IconButton>
              {loading ? (
                <div>
                  <CircularProgress className={classes.audioLoading} />
                </div>
              ) : (
                <RecordingTimer />
              )}
              <IconButton
                aria-label="sendRecordedAudio"
                component="span"
                onClick={handleUploadAudio}
                disabled={loading}
              >
                <CheckCircleOutlinedIcon className={classes.sendAudioIcon} />
              </IconButton>
            </div>
          ) : (
            <IconButton
              aria-label="showRecorder"
              component="span"
              disabled={loading || ticketStatus !== "open"}
              onClick={handleStartRecording}
            >
              <MicIcon className={classes.sendMessageIcons} />
            </IconButton>
          )}
        </div>
      </Paper>
    </>
  );
};

export default MessageInput;
